import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { findProductById } from '../utils/storeProducts';

const CART_STORAGE_KEY = 'mtsStoreCart';

const CartContext = createContext(null);

const readStoredCart = () => {
    try {
        const stored = localStorage.getItem(CART_STORAGE_KEY);
        if (!stored) return {};
        const parsed = JSON.parse(stored);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => readStoredCart());

    useEffect(() => {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const setQuantity = useCallback((productId, quantity) => {
        setItems((prev) => {
            const next = { ...prev };
            if (quantity > 0) {
                next[productId] = quantity;
            } else {
                delete next[productId];
            }
            return next;
        });
    }, []);

    const addItem = useCallback((productId, quantity = 1) => {
        setItems((prev) => ({
            ...prev,
            [productId]: (prev[productId] || 0) + quantity,
        }));
    }, []);

    const removeItem = useCallback((productId) => {
        setItems((prev) => {
            const next = { ...prev };
            delete next[productId];
            return next;
        });
    }, []);

    const clearCart = useCallback(() => {
        setItems({});
    }, []);

    const cartLines = useMemo(() => {
        return Object.entries(items)
            .map(([productId, quantity]) => {
                const product = findProductById(productId);
                if (!product) return null;
                return {
                    productId,
                    product,
                    quantity,
                    lineTotal: product.price * quantity,
                };
            })
            .filter(Boolean);
    }, [items]);

    const itemCount = useMemo(
        () => cartLines.reduce((sum, line) => sum + line.quantity, 0),
        [cartLines]
    );

    const subtotal = useMemo(
        () => cartLines.reduce((sum, line) => sum + line.lineTotal, 0),
        [cartLines]
    );

    const value = useMemo(
        () => ({
            items,
            cartLines,
            itemCount,
            subtotal,
            setQuantity,
            addItem,
            removeItem,
            clearCart,
        }),
        [items, cartLines, itemCount, subtotal, setQuantity, addItem, removeItem, clearCart]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
